import type { ConfirmReceiptLine, ReadyLine, ReadyOrder, ShortfallReason } from '../../types/Procurement';

/** The stock picker's value for "make a new stock item from this line". */
export const CREATE_STOCK = '__create__';

export const SHORTFALL_REASON_OPTIONS: Array<{ value: ShortfallReason; label: string }> = [
  { value: 'short_shipped', label: 'Short shipped' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'other', label: 'Other' },
];

export interface LineDraft {
  /** What arrived, as typed. Starts as the suggested quantity (ordered minus received). */
  quantity: string;
  reason: ShortfallReason | '';
  /** An existing stock item id, CREATE_STOCK, or '' when none is chosen yet. */
  stock: string;
}

export interface LineErrors { quantity?: string; reason?: string; stock?: string }

export type Drafts = Record<string, LineDraft>;

const QUANTITY = /^\d{1,9}(\.\d{1,3})?$/;

export const flattenLines = (orders: ReadyOrder[]): Array<ReadyLine & { order_id: string; order_label: string }> =>
  orders.flatMap(o => o.lines.map(l => ({ ...l, order_id: o.order_id, order_label: o.marketplace_order_id || o.seller_name || o.order_id.slice(0, 8).toUpperCase() })));

/** Every line starts as "all of the rest arrived", with the suggested stock item picked when there is one. */
export function initialDrafts(orders: ReadyOrder[]): Drafts {
  const out: Drafts = {};
  for (const l of orders.flatMap(o => o.lines)) {
    const s = l.stock_suggestion;
    out[l.line_id] = {
      quantity: String(l.suggested_quantity),
      reason: '',
      stock: !l.tracks_stock ? '' : l.stock_item_id ?? (s.mode === 'existing' && s.stock_item_id ? s.stock_item_id : s.mode === 'create' ? CREATE_STOCK : ''),
    };
  }
  return out;
}

export const quantityOf = (draft: LineDraft | undefined): number => {
  const n = Number(draft?.quantity);
  return Number.isFinite(n) ? n : 0;
};

/** A line is short when less than what was still to arrive was entered. */
export const isShort = (line: ReadyLine, draft: LineDraft | undefined): boolean =>
  QUANTITY.test((draft?.quantity ?? '').trim()) && quantityOf(draft) < line.remaining;

export function validateDrafts(lines: ReadyLine[], drafts: Drafts): Record<string, LineErrors> {
  const errors: Record<string, LineErrors> = {};
  for (const line of lines) {
    const d = drafts[line.line_id];
    const e: LineErrors = {};
    const text = (d?.quantity ?? '').trim();
    if (!QUANTITY.test(text)) e.quantity = 'Enter a number, at most 3 decimals.';
    else if (Number(text) > line.remaining) e.quantity = `At most ${line.remaining} is still to arrive.`;
    else {
      if (Number(text) < line.remaining && !d?.reason) e.reason = 'Say why it is short.';
      if (Number(text) > 0 && line.tracks_stock && !line.stock_item_id && !d?.stock) e.stock = 'Choose a stock item.';
    }
    if (e.quantity || e.reason || e.stock) errors[line.line_id] = e;
  }
  return errors;
}

/** The request lines: every line is sent, so a line that did not arrive is recorded as a shortfall, not left open. */
export function buildConfirmLines(lines: ReadyLine[], drafts: Drafts): ConfirmReceiptLine[] {
  return lines.map(line => {
    const d = drafts[line.line_id];
    const quantity = d.quantity.trim();
    const short = Number(quantity) < line.remaining;
    const out: ConfirmReceiptLine = { line_id: line.line_id, quantity };
    if (short && d.reason) out.shortfall_reason = d.reason;
    if (Number(quantity) > 0 && line.tracks_stock && !line.stock_item_id) {
      if (d.stock === CREATE_STOCK) out.create_stock_item = true;
      else if (d.stock) out.stock_item_id = d.stock;
    }
    return out;
  });
}
